import {observer} from 'mobx-react-lite';
import React, {useEffect, useState} from 'react';
import {useService} from '../../../../infrastructure/service/service';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select, SelectChangeEvent,
  TextField,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {UserDialogProps} from '../../../../entity/props/users/user-dialog-props';
import Skeleton from 'react-loading-skeleton';
import LoadingComponent from '../widget/LoadingComponent';
import {ManageMode} from '../../../../entity/enums/manage-mode';
import {DropdownOption} from '../../../../entity/core/dropdown-option';
import {getUserRoles, UserRole} from '../../../../entity/enums/user-role';
import {Organization} from '../../../../entity/model/organization';
import {UserDto} from '../../../../entity/dto/user-dto';
import moment from 'moment';
import {Form, Formik, FormikHelpers, FormikProps} from 'formik';
import {KeyValueResource} from '../../../../entity/resource/key-value-resource';

import '../../../../assets/css/style.css';
import '../../../../assets/css/user/user-dialog.css';

const UserDialog = (props: UserDialogProps): JSX.Element => {
  const {organizationService, userService} = useService();

  const [organization, setOrganization] = useState<Organization>();
  const [profileInfoData, setProfileInfoData] = useState<KeyValueResource []>([]);
  const [roles, setRoles] = useState<string[]>([]);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [rolesAccordion, setRolesAccordion] = useState<boolean>(false);
  const [permissionsAccordion, setPermissionsAccordion] = useState<boolean>(false);
  const [roleOptions, setRoleOptions] = useState<DropdownOption[]>([]);

  useEffect(() => {
    const init = async (): Promise<void> => {
      setLoading(true);

      const user = await userService.getCurrentUser();
      const organization = await organizationService.getOrganizationById(props.user?.organizationID ?? user?.organizationID ?? '');
      setOrganization(organization);

      if (organization)
        setRoleOptions(getUserRoles(organization.isPublic).map((role: string): DropdownOption => ({key: role, text: role, value: role})));

      if (props.user && organization) {
        setProfileInfoData([
          { label: 'First Name', value: props.user.firstName },
          { label: 'Last Name', value: props.user.lastName },
          { label: 'Email', value: props.user.email },
          { label: 'Organization', value: organization.name },
          { label: 'User ID', value: props.user.id },
          { label: 'Created Date', value: props.user.createdAt && new Date(props.user?.createdAt).toDateString() }
        ]);

        setRoles(props.user.roles);
        setPermissions(props.user.permissions);
      }

      setLoading(false);
    }

    init();
  }, []);

  const handleValidate = (values: UserDto): Record<string, string> => {
    const errors: Record<string, string> = {};

    if (!values.firstName)
      errors.firstName = 'First Name is required.';

    if (!values.lastName)
      errors.lastName = 'Last Name is required.';

    if (!values.email)
      errors.email = 'Email is required.';

    if (!values.roles)
      errors.roles = 'Role is required.';

    return errors;
  }

  const handleSubmit = async (values: UserDto, {setSubmitting}: FormikHelpers<UserDto>): Promise<void> => {
    setSubmitting(true);

    const userDto: UserDto = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      organizationID: organization?.id ?? '',
      roles: [values.roles],
      isPrimaryContact: values.isPrimaryContact
    };

    if (props.mode === ManageMode.CREATE)
      await userService.createUser(organization?.id ?? '', userDto);
    else
      await userService.updateUser(props.user?.id ?? '', userDto);

    setSubmitting(false);
    props.setOpen(false);
  }

  const renderUserView = (): JSX.Element => {
    return (
      <Paper elevation={0}>
        <Card elevation={0} className={'template-dialog-card info'}>
          <CardContent className={'user-info-card-content'}>
            {
              profileInfoData.map((data: KeyValueResource, index: number): JSX.Element =>
                <Box key={index} className={'template-item template'}>
                  <Typography id={`text-user-details-label-${index}`} className={'template-data-label user'}>{data.label}</Typography>
                  <Typography id={`text-user-details-value-${index}`} className={'template-data-value'}>{data.value ?? <Skeleton/>}</Typography>
                </Box>
              )
            }
          </CardContent>
        </Card>
        <Box>
          <Accordion
            elevation={0}
            className={`template-dialog-card ${!rolesAccordion ? ' list' : 'active'}`}
            expanded={rolesAccordion}
            onChange={(): void => setRolesAccordion(!rolesAccordion)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon className={'dialog-icon'}/>}>
              <Typography id={'text-user-roles-header'} className={'template-data-value accordion'}>Roles</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {
                roles.map((role: string, index: number): JSX.Element =>
                  <Typography id={`text-user-role-${index}`} key={index} className={`template-list-item child ${index % 2 === 0 ? 'even' : 'odd'} ${index === roles.length - 1 ? 'last' : ''}`}>
                    {role}
                  </Typography>
                )
              }
            </AccordionDetails>
          </Accordion>
          <Accordion
            elevation={0}
            className={`template-dialog-card ${!permissionsAccordion ? ' last' : 'last-active'}`}
            expanded={permissionsAccordion}
            onChange={(): void => setPermissionsAccordion(!permissionsAccordion)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon className={'dialog-icon'}/>}>
              <Typography id={'text-user-permissions-header'} className={'template-data-value accordion'}>User Permissions</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {
                permissions.map((permission: string, index: number): JSX.Element =>
                  <Typography id={`text-user-permission-${index}`} key={index} className={`template-list-item child ${index % 2 === 0 ? 'even' : 'odd'} ${index === permissions.length - 1 ? 'last' : ''}`}>
                    {permission}
                  </Typography>
                )
              }
            </AccordionDetails>
          </Accordion>
        </Box>
      </Paper>
    );
  }

  const renderUserForm = (): JSX.Element => {
    return (
      <Formik
        initialValues={userService.getUserDto(props.mode, organization?.id ?? '', props.user)}
        validate={handleValidate}
        onSubmit={handleSubmit}>
        {({values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting}: FormikProps<UserDto>): JSX.Element => (
          <Form onSubmit={handleSubmit}>
            <Card elevation={0} className={'template-dialog-card info'}>
              <CardContent className={'user-info-card-content'}>
                <Box className={`template-item ${touched.firstName && Boolean(errors.firstName) ? 'form' : 'template'}`}>
                  <Typography className={`template-data-label user ${touched.firstName && Boolean(errors.firstName) ? 'form' : 'view'}`}>First Name</Typography>
                  <TextField
                    id={'input-user-first-name'}
                    required
                    fullWidth
                    label='First Name'
                    name='firstName'
                    className={'template-input user'}
                    value={values.firstName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.firstName && Boolean(errors.firstName)}
                    helperText={touched.firstName && errors.firstName}/>
                </Box>
                <Box className={`template-item ${touched.lastName && Boolean(errors.lastName) ? 'form' : 'template'}`}>
                  <Typography className={`template-data-label user ${touched.lastName && Boolean(errors.lastName) ? 'form' : 'view'}`}>Last Name</Typography>
                  <TextField
                    id={'input-user-last-name'}
                    required
                    fullWidth
                    label='Last Name'
                    name='lastName'
                    className={'template-input user'}
                    value={values.lastName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.lastName && Boolean(errors.lastName)}
                    helperText={touched.lastName && errors.lastName}/>
                </Box>
                <Box className={`template-item ${touched.email && Boolean(errors.email) ? 'form' : 'template'}`}>
                  <Typography className={`template-data-label user ${touched.email && Boolean(errors.email) ? 'form' : 'view'}`}>Email</Typography>
                  <TextField
                    id={'input-user-email'}
                    required
                    fullWidth
                    disabled={props.mode === ManageMode.UPDATE}
                    label='Email'
                    name='email'
                    className={'template-input user'}
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}/>
                </Box>
                <Box className={`template-item ${touched.roles && Boolean(errors.roles) ? 'form' : 'template'}`}>
                  <Typography className={`template-data-label user ${touched.roles && Boolean(errors.roles) ? 'form' : 'view'}`}>Role</Typography>
                  <FormControl required fullWidth error={touched.roles && Boolean(errors.roles)} className={'template-input user'}>
                    <InputLabel>Role</InputLabel>
                    <Select
                      id={'input-user-role'}
                      name='roles'
                      value={values.roles}
                      label='Role'
                      onBlur={handleBlur}
                      onChange={(event: SelectChangeEvent) => handleChange({target: {name: event.target.name, value: event.target.value}})}>
                      {
                        roleOptions.map((option: DropdownOption): JSX.Element => <MenuItem key={option.key} value={option.value}>{option.text}</MenuItem>)
                      }
                    </Select>
                    <FormHelperText>{touched.roles && typeof errors.roles === 'string' ? errors.roles : ''}</FormHelperText>
                  </FormControl>
                </Box>
                {
                  values.roles === UserRole.ORG_ADMIN &&
                    <Box className={'user-primary-contact-checkbox'}>
                      <Typography className={'template-data-label user view'}>Primary Contact</Typography>
                      <FormControlLabel
                        control={<Checkbox
                          className={'user-primary-contact-checkbox-box'}
                          id={'input-user-primary-contact'}
                          name="isPrimaryContact"
                          checked={values.isPrimaryContact}
                          onChange={handleChange}
                          onBlur={handleBlur}/>}
                        label=""/>
                    </Box>
                }
                <Box className={'template-item template'}>
                  <Typography className={'template-data-label user view'}>Organization</Typography>
                  <TextField
                    id={'input-user-organization'}
                    required
                    fullWidth
                    disabled
                    className={'template-input user'}
                    value={organization?.name ?? ''}/>
                </Box>
                {
                  props.mode === ManageMode.UPDATE &&
                    <>
                      <Box className={'template-item template'}>
                        <Typography className={'template-data-label user view'}>User ID</Typography>
                        <TextField
                          id={'input-user-id'}
                          required
                          fullWidth
                          disabled
                          className={'template-input user'}
                          value={props.user?.id}/>
                      </Box>
                      <Box className={'template-item template'}>
                        <Typography className={'template-data-label user view'}>Created Date</Typography>
                        <TextField
                          id={'input-user-created-date'}
                          required
                          fullWidth
                          disabled
                          className={'template-input user'}
                          value={moment.utc(props.user?.createdAt).format('YYYY-MM-DD')}/>
                      </Box>
                    </>
                }
              </CardContent>
            </Card>
            <Divider sx={{ my: 2, mx: -3 }} />
            <Box className={'action-btn-container'}>
              <Button
                id={'btn-user-cancel'}
                disableTouchRipple
                onClick={(): void => props.setOpen(false)}
                className={'zitles-button cancel'}>
                Cancel
              </Button>
              <Button
                id={'btn-user-submit'}
                type='submit'
                disabled={isSubmitting}
                className={`zitles-button submit ${isSubmitting ? 'disable' : ''}`}
                startIcon={isSubmitting ? <CircularProgress size={14} color='inherit'/> : null}>
                {
                  isSubmitting ? `${props.mode === ManageMode.CREATE ? 'Creating' : 'Updating'}` : 'Submit'
                }
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    );
  }

  return (
    <>
      {
        loading
          ? <LoadingComponent/>
          : <Dialog open={props.open}>
              <DialogTitle className={'zitles-dialog-title'}>
                <span>
                  {
                    props.mode === ManageMode.VIEW
                      ? 'User Details'
                      : props.mode === ManageMode.CREATE
                        ? 'Create User'
                        : props.mode === ManageMode.UPDATE
                          ? 'Update User'
                          : 'My Profile'
                  }
                </span>
                <IconButton id={'btn-user-dialog-close'} onClick={(): void => props.setOpen(false)}>
                  <CloseIcon className={'dialog-icon'} />
                </IconButton>
              </DialogTitle>
              <DialogContent dividers className={'user-dialog-content'}>
                <Paper elevation={0}>
                  {
                    props.mode === ManageMode.VIEW || props.mode === ManageMode.PROFILE
                      ? renderUserView()
                      : renderUserForm()
                  }
                </Paper>
              </DialogContent>
            </Dialog>
      }
    </>
  );
}

export default observer(UserDialog);
